import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import type {
  FilterWrapperParams,
  IAfterGuiAttachedParams,
} from 'ag-grid-community';
import type { CustomFilterDisplayProps } from 'ag-grid-react';
import { useGridFilterDisplay } from 'ag-grid-react';
import { CheckboxField, SearchField } from '@wk/components-v3-react16';
import { useGetWorkflows } from '../../../RunOrchestration/RunOrchestrationsViews/utils/useGetWorkflows';
import { useOrchestrationFilter } from './useOrchestrationFilter';
import { useWorkflowSearch } from './useWorkflowSearch';
import type { OrchestrationNameFilterModel } from './types';
import styles from './OrchestrationNameFilter.module.scss';

interface OrchestrationNameFilterProperties
  extends CustomFilterDisplayProps<OrchestrationNameFilterModel>,
    FilterWrapperParams {}

const OrchestrationNameFilter = ({
  model,
  onStateChange,
  state,
}: OrchestrationNameFilterProperties) => {
  const searchInputReference = useRef<HTMLInputElement>(null);
  const selectAllInputReference = useRef<HTMLInputElement>(null);
  const { data: workflows, isLoading } = useGetWorkflows();

  const afterGuiAttached = useCallback(
    (parameters?: IAfterGuiAttachedParams) => {
      if (!parameters?.suppressFocus) {
        searchInputReference.current?.focus();
      }
    },
    []
  );

  useGridFilterDisplay({ afterGuiAttached });
  useOrchestrationFilter(model);

  const { filteredWorkflows, searchTerm, setSearchTerm } =
    useWorkflowSearch(workflows);

  const selectedWorkflowNames = useMemo(
    () => state.model?.workflowNames || [],
    [state.model?.workflowNames]
  );

  const isAllFilteredSelected = useMemo(
    () =>
      filteredWorkflows.length > 0 &&
      filteredWorkflows.every((workflow) =>
        selectedWorkflowNames.includes(workflow.name)
      ),
    [filteredWorkflows, selectedWorkflowNames]
  );

  const isIndeterminate = useMemo(() => {
    if (filteredWorkflows.length === 0) {
      return false;
    }
    const selectedFilteredCount = filteredWorkflows.filter((workflow) =>
      selectedWorkflowNames.includes(workflow.name)
    ).length;
    return selectedFilteredCount > 0 && !isAllFilteredSelected;
  }, [filteredWorkflows, selectedWorkflowNames, isAllFilteredSelected]);

  const selectAllAriaChecked = useMemo<boolean | 'mixed'>(() => {
    if (isIndeterminate) {
      return 'mixed';
    }
    return isAllFilteredSelected;
  }, [isIndeterminate, isAllFilteredSelected]);

  useEffect(() => {
    if (selectAllInputReference.current) {
      selectAllInputReference.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  const updateModel = useCallback(
    (workflowNames: string[]) => {
      const newModel = workflowNames.length > 0 ? { workflowNames } : null;
      onStateChange({ model: newModel });
    },
    [onStateChange]
  );

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const workflowName = event.target.value;
      const isSelected = selectedWorkflowNames.includes(workflowName);
      const updatedNames = isSelected
        ? selectedWorkflowNames.filter((name: string) => name !== workflowName)
        : [...selectedWorkflowNames, workflowName];

      updateModel(updatedNames);
    },
    [selectedWorkflowNames, updateModel]
  );

  const handleSelectAll = useCallback(() => {
    if (isAllFilteredSelected) {
      const filteredNamesSet = new Set(
        filteredWorkflows.map((workflow) => workflow.name)
      );
      const remainingNames = selectedWorkflowNames.filter(
        (name: string) => !filteredNamesSet.has(name)
      );
      updateModel(remainingNames);
    } else {
      const filteredNames = filteredWorkflows.map((workflow) => workflow.name);
      const allNames = [...selectedWorkflowNames, ...filteredNames];
      const uniqueNames = allNames.filter(
        (name: string, index: number) => allNames.indexOf(name) === index
      );
      updateModel(uniqueNames);
    }
  }, [
    isAllFilteredSelected,
    filteredWorkflows,
    selectedWorkflowNames,
    updateModel,
  ]);

  const handleKeydown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const spaceKeys = [' ', 'Space'];
      if (
        spaceKeys.includes(event.key) &&
        event.currentTarget.getAttribute('aria-disabled')
      ) {
        event.preventDefault();
      }
    },
    []
  );

  if (isLoading) {
    return <div className={styles.container}>Loading...</div>;
  }

  return (
    <div className={styles.container} tabIndex={-1}>
      <div className={styles.searchWrapper}>
        <SearchField
          searchButtonId="orchestration-search-button"
          size="large"
          hasClearIcon={searchTerm.length > 0}
          onClear={() => setSearchTerm('')}
        >
          <input
            ref={searchInputReference}
            type="search"
            name="orchestration-search"
            aria-labelledby="orchestration-search-button"
            autoComplete="off"
            onInput={(event) =>
              setSearchTerm((event.target as HTMLInputElement).value)
            }
            placeholder="Search orchestrations..."
            value={searchTerm}
          />
        </SearchField>
      </div>

      {filteredWorkflows.length > 0 && (
        <div className={styles.selectAllWrapper}>
          <CheckboxField
            labelFor="select-all-orchestrations"
            label="Select All"
            size="extra-small"
          >
            <input
              ref={selectAllInputReference}
              id="select-all-orchestrations"
              name="SelectAll"
              value="select-all"
              type="checkbox"
              checked={isAllFilteredSelected}
              aria-checked={selectAllAriaChecked}
              onChange={handleSelectAll}
              onKeyDown={handleKeydown}
              flow-id="checkbox"
            />
          </CheckboxField>
        </div>
      )}

      <div className={styles.checkboxList}>
        {filteredWorkflows.length > 0 ? (
          filteredWorkflows.map((workflow) => {
            const isChecked = selectedWorkflowNames.includes(workflow.name);
            const inputId = `orchestration-${workflow.name
              .replace(/\s+/g, '-')
              .toLowerCase()}`;
            return (
              <div key={workflow.name} className={styles.checkboxWrapper}>
                <CheckboxField
                  labelFor={inputId}
                  label={workflow.name}
                  size="extra-small"
                >
                  <input
                    id={inputId}
                    name={workflow.name}
                    value={workflow.name}
                    type="checkbox"
                    checked={isChecked}
                    onChange={handleCheckboxChange}
                    onKeyDown={handleKeydown}
                    flow-id="checkbox"
                  />
                </CheckboxField>
              </div>
            );
          })
        ) : (
          <div className={styles.noResults}>No orchestrations found</div>
        )}
      </div>
    </div>
  );
};

export default OrchestrationNameFilter;
